import React, { useState, useEffect } from 'react';
import SummaryApi from "../common";
import * as XLSX from 'xlsx';

const InstructorStudentReports = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const instructorId = Number(sessionStorage.getItem("instructor_id"));

  useEffect(() => {
    if (instructorId) {
      fetchStudentData();
    } else {
      setError("Instructor ID not found in session storage");
      setLoading(false);
    }
  }, [instructorId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(SummaryApi.enrollment_retrieve_by_instructor.url, {
        method: SummaryApi.enrollment_retrieve_by_instructor.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructor_id: instructorId })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      // FIXED: Ensure students is always an array
      setStudents(
        result.status && result.data
          ? Array.isArray(result.data)
            ? result.data
            : [result.data]  // Wrap single object into array
          : []
      );
    } catch (err) {
      setError(err.message);
      console.error('Error fetching student data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStudentName = (student) =>
    `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'N/A';

const getStatusBadge = (status) => {
  const isActive = status === 'Active';
  const bgColor = isActive 
    ? 'bg-emerald-100 border-emerald-300 text-emerald-800' 
    : 'bg-red-100 border-red-300 text-red-800';

  return (
    <span className={`px-3 py-1.5 rounded text-xs font-medium border ${bgColor}`}>
      {status}
    </span>
  );
};
  const formatMobileNumber = (mobile) => {
    // Show mobile number normally like 9999999999
    if (!mobile) return 'N/A';
    return mobile;
  };
  // Parse batch time from day-wise schedule
  const parseBatchSchedule = (enrollment) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const activeDays = [];

    days.forEach(day => {
      const slot = enrollment[day];
      if (slot && slot !== 'N/A') {
        const [start, end] = slot.split('-');
        if (start && end) {
          const formatTime = (t) => {
            const [h, m] = t.split(':');
            const hour = parseInt(h);
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
            return `${displayHour}:${m} ${period}`;
          };
          activeDays.push(`${day.charAt(0).toUpperCase() + day.slice(1, 3)} ${formatTime(start)} - ${formatTime(end)}`);
        }
      }
    });

    // ✅ each on new line using "\n"
    return activeDays.length > 0 ? activeDays.join('\n') : 'N/A';
  };

  // Updated: Extract Batch Name and Time from new structure
  const parseBatchInfo = (enrollment) => {
    const batchName = enrollment.batch_name || 'N/A';
    const batchTime = parseBatchSchedule(enrollment);
    return { name: batchName, time: batchTime };
  };

  // GROUPING: same student → one row, multiple batches in sub-rows
  const groupStudents = () => {
    const map = new Map(); // key: student_id (or mobile+name if no id)

    students.forEach((s) => {
      const key = s.student_id ?? `${s.mobile_number}-${getStudentName(s)}`;
      if (!map.has(key)) {
        map.set(key, {
          student: s,
          enrollments: []
        });
      }
      map.get(key).enrollments.push(s);
    });

    return Array.from(map.values());
  };

  // Pagination on grouped rows
  const groupedData = groupStudents();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = groupedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(groupedData.length / itemsPerPage);

  const paginate = (page) => setCurrentPage(page);

  const exportToExcel = () => {
    const data = groupedData.flatMap((group, groupIdx) => {
      return group.enrollments.map((s, i) => {
        const { name: batchName, time: batchTime } = parseBatchInfo(s);
        return {
          'S.No': groupIdx + 1,
          'Student Name': getStudentName(group.student),
          'Course Name': s.course_title || 'N/A',
          'Mobile Number': formatMobileNumber(group.student.mobile_number),
          'Enrolled Date': formatDate(s.enrollment_date),
          'Status': s.is_active === 1 ? 'Active' : 'Inactive',
          'Batch Name': batchName,
          'Batch Time': batchTime,
        };
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Student Reports');

    ws['!cols'] = [
      { wch: 8 },  // S.No
      { wch: 22 }, // Student Name
      { wch: 35 }, // Course Name
      { wch: 18 }, // Mobile
      { wch: 15 }, // Enrolled Date
      { wch: 12 }, // Status
      { wch: 25 }, // Batch Name
      { wch: 35 }  // Batch Time (wider for multiple days)
    ];

    XLSX.writeFile(wb, `student-reports-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getPageNumbers = () => {
    const max = 5;
    const pages = [];
    if (totalPages <= max) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const start = Math.max(1, currentPage - Math.floor(max / 2));
      const end = Math.min(totalPages, start + max - 1);
      for (let i = start; i <= end; i++) pages.push(i);
    }
    return pages;
  };

  // UI
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-base font-medium text-gray-600">Loading student data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 border border-gray-300 rounded p-4">
        <div className="text-base font-medium text-red-700 text-center">Error: {error}</div>
        <div className="mt-3 text-center">
          <button
            onClick={fetchStudentData}
            className="px-4 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 font-sans bg-gradient-to-br from-slate-50 to-gray-100 min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-800">Student Reports</h1>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="text-sm font-medium text-slate-600">
              Total Unique Students: <span className="font-bold text-indigo-600">{groupedData.length}</span>
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={exportToExcel}
                disabled={groupedData.length === 0}
                className="px-4 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Excel
              </button>
              <button
                onClick={fetchStudentData}
                className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm font-medium transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              {/* Header */}
              <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                <tr>
                  {[
                    'S.No',
                    'Student Name',
                    'Mobile Number',
                    'Course Name',
                    'Enrolled Date',
                    'Status',
                    'Batch Name',
                    'Batch Time',
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-4 text-center text-sm font-semibold uppercase tracking-wider ${i < 7 ? 'border-r border-indigo-500' : ''}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body */}
              <tbody className="divide-y divide-gray-200">
                {groupedData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-20 text-center">
                      <div className="text-lg font-medium text-gray-500">No students found.</div>
                    </td>
                  </tr>
                ) : (
                  currentGroups.map((group, groupIdx) => {
                    const baseIdx = indexOfFirstItem + groupIdx;
                    const rowSpan = group.enrollments.length;

                    return (
                      <React.Fragment key={group.student.enrollment_id ?? baseIdx}>
                        {group.enrollments.map((enrollment, subIdx) => {
                          const { name: batchName, time: batchTime } = parseBatchInfo(enrollment);

                          return (
                            <tr
                              key={enrollment.enrollment_id}
                              className="hover:bg-indigo-50 transition duration-150"
                            >
                              {/* S.No – only on first sub-row */}
                              {subIdx === 0 && (
                                <td
                                  rowSpan={rowSpan}
                                  className="px-5 py-4 text-center align-middle border-r border-gray-300 font-medium text-gray-800"
                                >
                                  {baseIdx + 1}
                                </td>
                              )}

                              {/* Student Name – merged */}
                              {subIdx === 0 && (
                                <td
                                  rowSpan={rowSpan}
                                  className="px-5 py-4 text-center align-middle border-r border-gray-300 font-medium text-indigo-900"
                                >
                                  {getStudentName(group.student)}
                                </td>
                              )}

                              {/* Mobile – merged */}
                              {subIdx === 0 && (
                                <td
                                  rowSpan={rowSpan}
                                  className="px-5 py-4 text-center align-middle border-r border-gray-300 text-gray-700"
                                >
                                  {formatMobileNumber(group.student.mobile_number)}
                                </td>
                              )}

                              {/* Course, Date, Status, Batch – per enrollment */}
                              <td className="px-5 py-4 text-center align-middle border-r border-gray-300">
                                <div className="max-w-xs truncate" title={enrollment.course_title}>
                                  <span className="text-sm text-gray-800">{enrollment.course_title || 'N/A'}</span>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-center align-middle border-r border-gray-300 text-sm text-gray-600">
                                {formatDate(enrollment.enrollment_date)}
                              </td>
                              <td className="px-5 py-4 text-center align-middle border-r border-gray-300">
                                {getStatusBadge(enrollment.is_active === 1 ? 'Active' : 'Inactive')}
                              </td>
                              <td className="px-5 py-4 text-center align-middle border-r border-gray-300">
                                <div className="max-w-xs truncate" title={batchName}>
                                  <span className="text-sm text-gray-700">{batchName}</span>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-center align-middle">
                                <div className="max-w-xs truncate" title={batchTime}>
                         <span className="text-sm text-gray-700 whitespace-pre-line">{batchTime}</span>

                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && groupedData.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-300">
              <p className="text-sm text-gray-600 mb-2 sm:mb-0 text-center sm:text-left">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-medium">{Math.min(indexOfLastItem, groupedData.length)}</span> of{' '}
                <span className="font-medium">{groupedData.length}</span> unique students
              </p>
              <nav className="flex items-center gap-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 rounded-l"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd" />
                  </svg>
                </button>

                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => paginate(p)}
                    className={`px-4 py-2 border text-sm font-medium transition ${
                      currentPage === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-indigo-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 rounded-r"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstructorStudentReports;